import { format, addDays, startOfDay } from 'date-fns';

/**
 * Generate an array of dates for a given range
 * @param startDate - Start date
 * @param days - Number of days to generate
 * @returns Array of Date objects
 */
export function generateDateRange(startDate: Date, days: number): Date[] {
  const dates: Date[] = [];
  for (let i = 0; i < days; i++) {
    dates.push(addDays(startDate, i));
  }
  return dates;
}

/**
 * Format date as YYYY-MM-DD
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatDateKey(date: Date): string {
  return format(startOfDay(date), 'yyyy-MM-dd');
}

/**
 * Get date key from Date object or string
 * @param date - Date object or ISO string
 * @returns YYYY-MM-DD formatted string
 */
export function getDateKey(date: Date | string): string {
  if (typeof date === 'string') {
    return formatDateKey(new Date(date));
  }
  return formatDateKey(date);
}

/**
 * Calculate minutes between two dates
 * @param start - Start date/time
 * @param end - End date/time
 * @returns Minutes between dates
 */
export function calculateMinutes(start: Date, end: Date): number {
  return Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60));
}

/**
 * Check if a date falls within a given range
 * @param date - Date to check
 * @param start - Range start
 * @param end - Range end
 * @returns True if date is within range
 */
export function isDateInRange(date: Date, start: Date, end: Date): boolean {
  const dateTime = date.getTime();
  return dateTime >= start.getTime() && dateTime <= end.getTime();
}

/**
 * Get today at midnight (start of day)
 * @returns Date object representing start of today
 */
export function getToday(): Date {
  return startOfDay(new Date());
}

/**
 * Get default date range for capacity view (today + 30 days)
 * @returns Object with start and end dates
 */
export function getDefaultDateRange(): { start: Date; end: Date } {
  const start = getToday();
  const end = addDays(start, 30);
  return { start, end };
}
